import { isCancelledError, useQueryClient } from "@tanstack/react-query"
import { type ErrorComponentProps, useRouter } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { type User, userKeys } from "@/entities/user"
import * as m from "@/paraglide/messages"
import { isAbortError, isRequestError } from "@/shared/api/errors"
import WarningIcon from "@/shared/assets/coolicons/Warning/Triangle_Warning.svg?react"
import TelegramLogo from "@/shared/assets/icons/Telegram_X_2019_Logo.svg?react"
import { collectDiagnostics } from "@/shared/lib/diagnostics"
import { copyToClipboard } from "@/shared/lib/platform"
import { openExternalLink } from "@/shared/lib/telegram"
import { RequestFailed } from "@/shared/ui/request-failed"
import { SecondaryButton } from "@/shared/ui/secondary-button"

export function ErrorFallback({ error }: ErrorComponentProps) {
	if (isAbortError(error) || isCancelledError(error) || isRequestError(error)) {
		return <RouteRequestFailed />
	}

	return <AppCrash error={toError(error)} />
}

function toError(error: unknown): Error {
	if (error instanceof Error) return error
	return new Error(String(error))
}

// router.invalidate() выдаёт новый match, а по нему CatchBoundary сбрасывается
// сам — свой reset() тут только добавил бы лишний цикл throw/catch.
function RouteRequestFailed() {
	const router = useRouter()

	return <RequestFailed onRetry={() => router.invalidate()} />
}

const COPY_LABEL = {
	idle: m.errorFallbackBtnCopy,
	copied: m.errorFallbackCopied,
	failed: m.errorFallbackCopyFailed,
}

function AppCrash({ error }: Pick<ErrorComponentProps<Error>, "error">) {
	const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
		"idle",
	)
	const [meta, setMeta] = useState("")
	const queryClient = useQueryClient()

	// Поля юзера — best-effort: на раннем краше (упал loader/auth до загрузки)
	// кэш пуст, и они просто не выводятся.
	useEffect(() => {
		let cancelled = false

		const user = queryClient
			.getQueriesData<User>({ queryKey: userKeys.all })
			.map(([, value]) => value)
			.find(
				(value): value is User =>
					typeof value === "object" &&
					value !== null &&
					"luni_name" in value &&
					"id" in value,
			)

		collectDiagnostics(user).then((text) => {
			if (!cancelled) setMeta(text)
		})

		return () => {
			cancelled = true
		}
	}, [queryClient])

	const handleTelegramContact = () => {
		openExternalLink("https://t.me/lunisupport", { tryInstantView: false })
	}

	const handleCopy = async () => {
		if (!error) return
		const errorText = [meta, `${error.message}\n\n${error.stack || ""}`]
			.filter(Boolean)
			.join("\n\n")
		setCopyState((await copyToClipboard(errorText)) ? "copied" : "failed")
		setTimeout(() => setCopyState("idle"), 2000)
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black">
			{/* Content */}
			<div className="relative z-10 flex max-w-md flex-col items-center px-6">
				{/* Warning Icon */}
				<WarningIcon className="size-14 text-white" aria-hidden="true" />

				{/* Title */}
				<h1 className="mt-11 w-full text-center text-[32px] whitespace-pre-line text-white">
					{m.errorFallbackTitle()}
				</h1>

				{/* Description */}
				<p className="mt-11 w-full text-center text-base leading-snug whitespace-pre-line text-white">
					{m.errorFallbackDescription()}
				</p>

				{/* Buttons */}
				<div className="mt-11 flex w-full flex-col gap-4">
					{/* Telegram Contact Button */}
					<SecondaryButton
						type="button"
						variant="transparent"
						onClick={handleTelegramContact}
						className="h-16"
					>
						<TelegramLogo className="mr-4 size-8" />
						@lunisupport
					</SecondaryButton>
				</div>
			</div>

			{/* Error details - positioned at bottom */}
			{error && (
				<details className="group fixed inset-x-4 bottom-8 z-50">
					<summary className="flex cursor-pointer items-center justify-center gap-1.5 text-xs text-white/20 transition-colors hover:text-white/40 [&::-webkit-details-marker]:hidden [&::marker]:hidden">
						<span className="inline-block transition-transform duration-200 group-open:rotate-180">
							▲
						</span>
						{m.errorFallbackDetailsToggle()}
					</summary>
					<div className="absolute inset-x-0 bottom-6 flex max-h-[50vh] flex-col overflow-hidden rounded-lg border border-white/10 bg-black/95 shadow-xl backdrop-blur-sm">
						<div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
							<span className="text-xs text-white/40">
								{m.errorFallbackDetailsTitle()}
							</span>
							<button
								type="button"
								onClick={handleCopy}
								className="rounded px-2 py-1 text-[10px] text-white/60 transition-colors hover:bg-white/10 hover:text-white/80"
							>
								{COPY_LABEL[copyState]()}
							</button>
						</div>
						<div className="overflow-auto">
							{meta && (
								<pre className="border-b border-white/10 p-3 text-[11px] leading-snug whitespace-pre-wrap text-white/70">
									<code className="select-all">{meta}</code>
								</pre>
							)}
							<pre className="p-3 text-[10px] leading-tight text-white/50">
								<code className="select-all">
									{error.message}
									{"\n\n"}
									{error.stack}
								</code>
							</pre>
						</div>
					</div>
				</details>
			)}
		</div>
	)
}
